import React from 'react';
import { Box, Container } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';

const PageSection = styled(Box)`
  min-height: 100vh;
  padding: 120px 0 80px;
  background: linear-gradient(135deg, #000B18 0%, #001529 100%);
  color: #fff;
`;

const BackgroundPattern = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.2;
  z-index: 1;
`;

const Content = styled(Container)`
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;

const Section = styled.div`
  margin-bottom: 3rem;
  background: rgba(10, 25, 41, 0.5);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid rgba(79, 172, 254, 0.2);
  backdrop-filter: blur(10px);

  h2 {
    font-size: 2rem;
    color: #4facfe;
    margin-bottom: 1.5rem;
    font-weight: 600;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
  }

  ul {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    padding-left: 2rem;

    li {
      margin-bottom: 1rem;
    }
  }
`;

const EffectiveDate = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  margin: -1rem 0 3rem;
`;

const ContactInfo = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  background: rgba(79, 172, 254, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(79, 172, 254, 0.2);

  p {
    margin-bottom: 0.5rem;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-top: 4rem;
`;

const TermsOfService = () => {
  return (
    <>
      <Navbar />
      <PageSection>
        <BackgroundPattern />
        <Content maxWidth="lg">
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Terms of Service
          </Title>
          <EffectiveDate>Effective Date: April 2025</EffectiveDate>

          <Section>
            <h2>1. AGREEMENT TO TERMS</h2>
            <p>
              By accessing or using services provided by Yurix Ventures Private Limited ("Yurix," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms") and all applicable laws and regulations. If you do not agree with any of these Terms, you are prohibited from using or accessing our services.
            </p>
          </Section>

          <Section>
            <h2>2. SERVICES OVERVIEW</h2>
            <p>Yurix provides AI and data science consulting services, including but not limited to:</p>
            <ul>
              <li>Machine learning solutions</li>
              <li>Data analytics and visualization</li>
              <li>Digital transformation services</li>
              <li>AI implementation and integration</li>
              <li>Custom software development</li>
            </ul>
            <p>We reserve the right to modify, enhance, suspend, or discontinue any aspect of our services at our discretion.</p>
          </Section>

          <Section>
            <h2>3. USE LICENSE</h2>
            <p>Yurix grants you a limited, non-exclusive, non-transferable, revocable license to access and use our services for personal or business purposes in accordance with these Terms. Under this license, you may not:</p>
            <ul>
              <li>Modify, copy, or create derivative works based on our materials or services</li>
              <li>Use our services for any illegal or unauthorized purpose</li>
              <li>Attempt to decompile, reverse engineer, or disassemble any software contained within our services</li>
              <li>Remove any copyright, trademark, or other proprietary notices</li>
              <li>Transfer, sell, or sublicense the materials or access to our services to any third party</li>
            </ul>
          </Section>

          <Section>
            <h2>4. INTELLECTUAL PROPERTY RIGHTS</h2>
            <p>
              All content, features, and functionality of Yurix's services, including but not limited to text, graphics, code, logos, icons, images, data compilations, and software, are the exclusive property of Yurix or our licensors and are protected by Indian and international copyright, trademark, patent, and other intellectual property laws.
            </p>
          </Section>

          <Section>
            <h2>5. CLIENT RESPONSIBILITIES</h2>
            <p>When using our services, you agree to:</p>
            <ul>
              <li>Provide accurate and complete information as required</li>
              <li>Maintain the confidentiality of any access credentials provided to you</li>
              <li>Promptly notify us of any unauthorized use of your account or other security breaches</li>
              <li>Use our services in compliance with all applicable laws and regulations</li>
            </ul>
          </Section>

          <Section>
            <h2>6. CONFIDENTIALITY</h2>
            <p>
              Both parties agree to maintain the confidentiality of any proprietary or sensitive information shared during the course of our relationship. Unless otherwise agreed in writing, neither party shall disclose confidential information to any third party.
            </p>
          </Section>

          <Section>
            <h2>7. PAYMENT TERMS</h2>
            <p>
              Payment terms will be specified in separate service agreements or statements of work. Unless otherwise stated, all fees are quoted in Indian Rupees and are non-refundable.
            </p>
          </Section>

          <Section>
            <h2>8. LIMITATION OF LIABILITY</h2>
            <p>
              To the maximum extent permitted by law, Yurix shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, or goodwill, resulting from:
            </p>
            <ul>
              <li>Your use or inability to use our services</li>
              <li>Any unauthorized access to or use of our servers or personal information</li>
              <li>Any interruption or cessation of transmission to or from our services</li>
              <li>Any bugs, viruses, or other harmful code transmitted through our services</li>
            </ul>
            <p>
              In no event shall our total liability exceed the amount paid by you to Yurix for the services in question during the six (6) months prior to the claim.
            </p>
          </Section>

          <Section>
            <h2>9. INDEMNIFICATION</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Yurix and our affiliates, directors, officers, employees, and agents from and against any claims, liabilities, damages, losses, costs, or expenses arising out of or in any way connected with your use of our services or violation of these Terms.
            </p>
          </Section>

          <Section>
            <h2>10. TERMINATION</h2>
            <p>
              We may terminate or suspend your access to our services immediately, without prior notice or liability, for any reason whatsoever, including, without limitation, if you breach these Terms.
            </p>
          </Section>

          <Section>
            <h2>11. GOVERNING LAW</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes relating to these Terms or your use of our services will be subject to the exclusive jurisdiction of the courts in Lucknow, Uttar Pradesh, India.
            </p>
          </Section>

          <Section>
            <h2>12. CHANGES TO TERMS</h2>
            <p>
              Yurix reserves the right to modify or replace these Terms at any time. We will provide notice of any significant changes through our website or direct communication. Your continued use of our services after such changes constitutes your acceptance of the new Terms.
            </p>
          </Section>

          <Section>
            <h2>13. CONTACT INFORMATION</h2>
            <p>For any questions regarding these Terms, please contact us at:</p>
            <ContactInfo>
              <p>Yurix Ventures Private Limited</p>
              <p>Lucknow, Uttar Pradesh, India</p>
              <p>Email: yurixventures@gmail.com</p>
              <p>Phone: +91 8756992565</p>
            </ContactInfo>
          </Section>

          <LastUpdated>Last Updated: April 2025</LastUpdated>
        </Content>
      </PageSection>
    </>
  );
};

export default TermsOfService; 